/* eslint-disable react/prop-types */
// TODO: Define the chart options in propTypes and remove the above eslint comment.
import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { isFunction, noop } from 'lodash';
import moment from 'moment-timezone';
import { useTheme } from '@material-ui/core/styles';
import Highcharts from 'highcharts';
import extendHighcharts from 'highcharts/highcharts-more';
import HighchartsReact from 'highcharts-react-official';
import DarkUnica from 'highcharts/themes/dark-unica';
import accessibility from 'highcharts/modules/accessibility';

import { useTimezone } from '../../../stores/SessionStore';
import useSize from '../../../utilities/UseSize';

// Clear Highcharts warning: Consider including the "accessibility.js" module to make your chart more usable for people with disabilities. Set the "accessibility.enabled" option to false to remove this warning
accessibility(Highcharts);
extendHighcharts(Highcharts);

function Chart({ initialHeight, options, onResize, chartRef, ...otherProps }, ref) {
    const containerRef = useRef();
    const internalChartRef = useRef();
    const initialHeightRef = useRef();
    const { width, height } = useSize(containerRef);
    const [timezone] = useTimezone();
    const theme = useTheme();
    const emptyOrNoData = height === 0 || options?.series?.length === 0;

    initialHeightRef.current = initialHeight;

    // Apply highcharts dark theme
    if (theme.palette.type === 'dark') {
        DarkUnica(Highcharts);
    }

    Highcharts.setOptions({
        time: {
            getTimezoneOffset(timestamp) {
                return -moment.tz(timestamp, timezone).utcOffset();
            },
        },
        lang: {
            decimalPoint: '.',
            thousandsSep: ',',
        },
    });

    // Update the UTC offset when the timezone changes.
    useEffect(() => {
        Highcharts.setOptions({
            time: {
                getTimezoneOffset(timestamp) {
                    // The UTC offset returned by moment must be inverted.  From the moment-timezone docs:
                    // "POSIX compatibility requires that the offsets are inverted. Therefore, Etc/GMT-X
                    //  will have an offset of +X and Etc/GMT+X will have an offset of -X. This is a result
                    //  of IANA's Time Zone Database and not an arbitrary choice by Moment.js."
                    return -moment.tz(timestamp, timezone).utcOffset();
                },
            },
        });
    }, [timezone]);

    // Reflow the chart when the container size changes (i.e. the sidebar is opened/closed).
    useLayoutEffect(() => {
        if (internalChartRef?.current?.chart) {
            internalChartRef.current.chart.setSize(width, height || initialHeightRef.current);
            onResize(internalChartRef.current.chart);
        }
    }, [width, height, onResize]);

    const setContainerRefs = useCallback(
        (containerEl) => {
            // Forward the reference to the container if requested.
            if (isFunction(ref)) ref(containerEl);

            // Save it in our local ref for use by the useSize hook.
            containerRef.current = containerEl;
        },
        [ref]
    );

    const setChartRef = useCallback(
        (chartEl) => {
            if (!chartEl) return;
            internalChartRef.current = chartEl;
            if (chartRef) {
                if (isFunction(chartRef)) chartRef(chartEl);
                else chartRef.current = chartEl;
            }
        },
        [chartRef]
    );

    return (
        <div className="chart-container" ref={setContainerRefs} {...otherProps}>
            {emptyOrNoData && !initialHeight ? (
                <div ref={internalChartRef} />
            ) : (
                <HighchartsReact highcharts={Highcharts} options={options} ref={setChartRef} />
            )}
        </div>
    );
}

const ForwardRef = forwardRef(Chart);

ForwardRef.defaultProps = {
    onResize: noop,
};

export default ForwardRef;
